import { shortMonthDate } from "../../../../../../Helpers/Functions/dateFormat"
import {
    APIInvoiceModel,
    APIPaymentMethodModel,
    APIPaymentMethodStatus,
    APIBillingAddressModel
} from "../../../../../../Models/API/BillingModel"
import { State, MainThunkDispatch } from "../../../../../../Reducers"
import {
    registerStripePaymentMethod,
    deletePaymentMethod
} from "../../../../../../Actions/API/BillingActions"
import { connect } from "react-redux"
import { Loader, Card, Text, Pill, ModalPanel, Box } from "dash-styled"
import { PaymentMethodListProvider } from "../../../../../Providers/PaymentMethodList/PaymentMethodList"
import { StripeProvider, Elements } from "react-stripe-elements"
import { PaymentMethodForm } from "./PaymentMethodForm/PaymentMethodForm"
import { closeModal } from "../../../../../../Actions/Client/ModalActions"
import { failMessage } from "../../../../../../Actions/Client/MessagesActions"
import { TrackingType } from "../../../../../../Actions/Types"
import { PaymentMethodRow } from "./PaymentMethodRow"

interface UserBillingProps {}

type APIPaymentMethodModelExtended = APIPaymentMethodModel & {
    isPrimary: boolean
}

interface StateProps {
    invoices: APIInvoiceModel[]
    paymentMethods: APIPaymentMethodModelExtended[]
    stripeKey: string
}

interface DispatchProps {
    doRegisterStripePaymentMethod: (
        last4Digits: string,
        tokenId: string,
        address: APIBillingAddressModel,
        priority: number,
        isFirst: boolean
    ) => Promise<void>
    doDeletePaymentMethod: (methodID: number) => Promise<void>
    showErrorMessage: (message: string) => void
    close: () => void
}

export type Props = UserBillingProps & StateProps & DispatchProps

export const UserBillingIsolated = ({
    invoices,
    showErrorMessage,
    stripeKey,
    doRegisterStripePaymentMethod,
    doDeletePaymentMethod,
    close
}: Props) => {
    const paymentMethodCardView = (method: APIPaymentMethodModel) => {
        const handleClickDelete = () => doDeletePaymentMethod(method.id)

        return (
            <PaymentMethodRow
                key={method.id}
                model={method}
                onDelete={handleClickDelete}
            />
        )
    }

    const invoiceCardView = (invoice: APIInvoiceModel) => {
        return (
            <Card>
                <Text>Amount: ${invoice.amount}</Text>
                <Text>Start: {shortMonthDate(invoice.periodStart)}</Text>
                <Text>End: {shortMonthDate(invoice.periodEnd)}</Text>
                <Text>Created: {shortMonthDate(invoice.created)}</Text>
                <Text>Updated: {shortMonthDate(invoice.updated)}</Text>
                <Pill
                    label={invoice.status}
                    color={invoice.status === "PAID" ? "coldgreen" : "red"}
                />
            </Card>
        )
    }
    const renderPaymentMethodSection = (
        paymentMethods?: APIPaymentMethodModel[]
    ) => {
        if (paymentMethods === undefined)
            return <Loader className="m-32 w-auto" />

        const handleSubmitStripePaymentMethod = async (
            last4Digits: string,
            tokenId: string,
            address: APIBillingAddressModel
        ) => {
            const maxPriority = paymentMethods.reduce((passed, method) => {
                return Math.max(passed, method.priority)
            }, 0)

            await doRegisterStripePaymentMethod(
                last4Digits,
                tokenId,
                address,
                maxPriority + 1,
                !paymentMethods || paymentMethods.length === 0
            )
        }

        return (
            <>
                <Box marginBottom="7">
                    <h2>Add payment method</h2>
                    <StripeProvider apiKey={stripeKey}>
                        <Elements>
                            <PaymentMethodForm
                                onSubmit={handleSubmitStripePaymentMethod}
                                onError={showErrorMessage}
                            />
                        </Elements>
                    </StripeProvider>
                </Box>
                <Box display="flex" alignItems="center" flexDirection="column">
                    {paymentMethods.map(paymentMethod =>
                        paymentMethodCardView(paymentMethod)
                    )}
                </Box>
            </>
        )
    }

    return (
        <ModalPanel onClose={close} testId="billing-modal">
            <PaymentMethodListProvider render={renderPaymentMethodSection} />
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mb-8">
                {invoices.map(invoiceCardView)}
            </div>
        </ModalPanel>
    )
}

const mapStateToProps = (state: State): StateProps => {
    return {
        invoices: state.api.billing.invoice.list,
        paymentMethods: (state.api.billing.paymentMethod.list || [])
            .sort((one, another) => {
                if (one.status === APIPaymentMethodStatus.Inactive) return 1
                if (another.status === APIPaymentMethodStatus.Inactive)
                    return -1
                return another.priority - one.priority
            })
            .map((paymentMethod, index) => ({
                ...paymentMethod,
                isPrimary: index === 0
            })),
        stripeKey: state.client.endpoints.stripe.publishableKey
    }
}

const mapDispatchToProps = (dispatch: MainThunkDispatch): DispatchProps => {
    return {
        doRegisterStripePaymentMethod: async (
            last4Digits: string,
            tokenId: string,
            address: APIBillingAddressModel,
            priority: number,
            isFirst: boolean
        ) => {
            const wasSuccessful = await dispatch(
                registerStripePaymentMethod(
                    last4Digits,
                    tokenId,
                    address,
                    priority
                )
            )

            if (wasSuccessful) {
                dispatch(closeModal())
                if (isFirst) dispatch({ type: TrackingType.billing_addMethod })
            }
        },
        doDeletePaymentMethod: (methodID: number) =>
            dispatch(deletePaymentMethod(methodID)),
        showErrorMessage: (message: string) => dispatch(failMessage(message)),
        close: () => dispatch(closeModal())
    }
}

export const UserBilling = connect(
    mapStateToProps,
    mapDispatchToProps
)(UserBillingIsolated)
